import {setDefaultAirlineParameter, setFlightSchedule} from '../slice/ciSlice';
import {baseQueryWithReAuth} from './config';
import {
  CiResultItem,
  IDefaultAirlineParameter,
  IFlightSchedule
} from '@/types/ci';
import {transformAirline} from '@/utils/flight';
import {createApi} from '@reduxjs/toolkit/query/react';

export const ciApi = createApi({
  reducerPath: 'ciApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: ['ci-calc'],
  endpoints: (builder) => ({
    getDefaultAirlineParameter: builder.query<
      IDefaultAirlineParameter[],
      {airline: string}
    >({
      query: ({airline}) => ({
        url: '/ATLAS_ci_get_airline_parameters',
        body: {
          airline: transformAirline(airline)
        },
        method: 'POST'
      }),
      async onQueryStarted(_args, {dispatch, queryFulfilled}) {
        try {
          const data = await queryFulfilled;
          if (data) {
            dispatch(setDefaultAirlineParameter(data.data));
          }
        } catch (error) {
          throw new Error('Error getDefaultAirlineParameter');
        }
      }
    }),
    postAirlineParameter: builder.mutation<
      null,
      {airline: string; airlineDefaults: IDefaultAirlineParameter}
    >({
      query: ({airline, airlineDefaults}) => ({
        url: '/ATLAS_ci_post_airline_parameters',
        body: {
          airline: transformAirline(airline),
          airline_defaults: airlineDefaults
        },
        method: 'POST'
      })
    }),
    getFlightSchedule: builder.query<
      IFlightSchedule,
      {airline: string; season: string}
    >({
      query: ({airline, season}) => ({
        url: '/ATLAS_ci_get_sectors_ci',
        body: {
          airline: transformAirline(airline),
          season
        },
        method: 'POST'
      }),
      async onQueryStarted(_args, {dispatch, queryFulfilled}) {
        try {
          const data = await queryFulfilled;
          if (data) {
            dispatch(setFlightSchedule(data.data));
          }
        } catch (error) {
          throw new Error('Error getFlightSchedule');
        }
      }
    }),
    getSectorDetail: builder.query<
      {
        sector_ci_data: Record<string, Record<string, CiResultItem>>;
        load_factor_sector: Record<string, number>;
      },
      {airline: string; sector: string; season: string}
    >({
      query: ({airline, sector, season}) => ({
        url: '/ATLAS_ci_get_sector_details',
        body: {
          airline: transformAirline(airline),
          sector,
          season
        },
        method: 'POST'
      })
    }),
    postLoadFactor: builder.mutation<
      null,
      {
        airline: string;
        sector: string;
        season: string;
        loadFactor: number;
        fleet: string;
      }
    >({
      query: ({airline, sector, season, loadFactor, fleet}) => ({
        url: '/ATLAS_ci_post_load_factor_sector',
        body: {
          airline: transformAirline(airline),
          sector,
          season,
          load_factor: loadFactor,
          fleet
        },
        method: 'POST'
      })
    })
  })
});

export const {
  useLazyGetDefaultAirlineParameterQuery,
  usePostAirlineParameterMutation,
  useLazyGetFlightScheduleQuery,
  useLazyGetSectorDetailQuery,
  usePostLoadFactorMutation
} = ciApi;
